import React, { FC, useState } from 'react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import { PunishmentHistoryModel, TrainingHistoryModel } from '../../../../shared/models/employee.model';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCurrentEmployee,
    getCurrentEmployeeEditProfile,
    getEmployeeAction
} from '../../../../shared/services/employee-service';
import TableRowNoData from '../../../../shared/components/molecules/commons/table-row-no-data';
import {
    getFixValue,
    handleEmployeeSectionInputChange,
    handleEmployeeSectionRemoveByIndex
} from '../../../../shared/utils/function-state';
import DeleteConfirmationModal from '../../../../shared/components/molecules/modals/delete-confirmation-modal';
import DateInput from '../../../../shared/components/atoms/date-input';
import { convertDateToString } from '../../../../shared/utils/functions-date-time';
import SectionFileForm from '../section-file-form';
import { SortingField } from '../../../../shared/models/common.model';
import TableHeaderOrdering, { Header, OrderData } from '../../../../shared/components/table-header-ordering';
import { SortingByField } from '../../../../shared/utils/sorting';

interface PunishmentHistoryRowProps extends PunishmentHistoryModel {
    index: number;
}

const PunishmentHistoryForm: FC = () => {
    const employeeAction = useSelector(getEmployeeAction);
    const employeeStateTarget = employeeAction.type === 'view-edit-request' ? getCurrentEmployeeEditProfile : getCurrentEmployee;
    const employee = useSelector(employeeStateTarget);
    const showOrder = employeeAction.type === 'view' || employeeAction.type === 'view-edit-request';
    const [sorting, setSorting] = useState<SortingField>({
        field: null,
        order: null
    });

    const tableRowNoData = () => {
        if (employee.punishment_histories.length === 0) {
            return <TableRowNoData colspan={5}/>;
        }
        return null;
    };

    const headers: Header[] = [
        {
            label: 'เลขที่คำสั่ง',
            field: 'order_no',
            order: null,
            isOrder: showOrder,
        },
        {
            label: 'เรื่อง',
            field: 'name',
            order: null,
            isOrder: showOrder,
        },
        {
            label: 'วันที่',
            field: 'date',
            order: null,
            isOrder: showOrder,
        },
        {
            label: 'แนบไฟล์',
            field: 'file',
            order: null,
            isOrder: false,
        },
        {
            label: '',
            field: '',
            order: null,
            isOrder: false,
        }
    ];

    const onChangeOrder = (orderData: OrderData) => {
        setSorting({
            field: orderData.field,
            order: orderData.order
        });
    };

    const dataOrder = () => {
        if (showOrder)
            return employee.punishment_histories.map(data => data).sort((a, b) => {
                return SortingByField(a, b, sorting);
            });
        return employee.punishment_histories;
    };

    return (
        <div className="h-100 overflow-x">
            <Table bordered>
                <thead>
                <TableHeaderOrdering headers={headers} onChangeOrder={onChangeOrder} isShow={showOrder}/>
                </thead>
                <tbody>
                {
                    dataOrder().map((data, index) => {
                        return <PunishmentHistoryRow
                            key={index}
                            index={index}
                            {...data}
                        />;
                    })
                }
                {tableRowNoData()}
                </tbody>
            </Table>
        </div>
    );
};

const PunishmentHistoryRow: FC<PunishmentHistoryRowProps> = ({
                                                                 index,
                                                                 date,
                                                                 name,
                                                                 order_no,
                                                                 file,
                                                                 from_id,
                                                             }) => {
    const dispatch = useDispatch();
    const employeeAction = useSelector(getEmployeeAction);
    const disable = employeeAction.type === 'view' || employeeAction.type === 'view-edit-request';
    const section_type = 'punishment_histories';
    const employeeStateTarget = employeeAction.type === 'view-edit-request' ? getCurrentEmployeeEditProfile : getCurrentEmployee;
    const employee = useSelector(employeeStateTarget);
    const oldEmployee = useSelector(getCurrentEmployee);
    let fromEmployee: any | null = null;

    if (from_id) {
        const found = oldEmployee[section_type].find(data => data.id === from_id);
        if (found)
            fromEmployee = found;
    }

    const validateEditRequest = (value: any, key: keyof PunishmentHistoryModel) => {
        if (employeeAction.type === 'view-edit-request') {
            if (fromEmployee && from_id) {
                if (value !== fromEmployee[key])
                    return 'field-warning';
            }
            if (!from_id) {
                return 'field-new';
            }
        }
        return '';
    }

    const [showModal, setShowModal] = useState(false);

    const deleteButton = () => {
        if (!disable) {
            return <FontAwesomeIcon icon={faCircleXmark} className="text-danger pointer" onClick={handleDelete}/>;
        }
        return null;
    };

    const handleDelete = () => {
        setShowModal(true);
    };

    const handleConfirmDelete = async () => {
        handleEmployeeSectionRemoveByIndex(section_type, index, dispatch);
        setShowModal(false);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    return (
        <tr className="bg-white">
            <td className={`${validateEditRequest(order_no, 'order_no')}`}>
                <input type="text" className="form-control" name="order_no" disabled={disable}
                       placeholder="" value={order_no || ''} required={true}
                       onChange={(e) => handleEmployeeSectionInputChange(section_type, index, e, dispatch)}/>
            </td>
            <td className={`${validateEditRequest(name, 'name')}`}>
                <input type="text" className="form-control" name="name" disabled={disable}
                       placeholder="" value={name || ''} required={true}
                       onChange={(e) => handleEmployeeSectionInputChange(section_type, index, e, dispatch)}/>
            </td>
            <td className={`${validateEditRequest(date, 'date')}`}>
                <DateInput date={date ? new Date(date) : null} required={true}
                           positionAbsolute={false}
                           onChange={
                               (value: Date | null) => handleEmployeeSectionInputChange(
                                   section_type,
                                   index,
                                   getFixValue('date', value ? convertDateToString(value) : null),
                                   dispatch
                               )
                           }
                           disable={disable}/>
            </td>
            <td className={`${validateEditRequest(file, 'file')}`}>
                <SectionFileForm section_key={'punishment_histories'} index={
                    disable ? employee.punishment_histories.findIndex(data =>
                        data.date === date &&
                        data.name === name &&
                        data.order_no === order_no
                    ) : index
                }/>
            </td>
            <td className="text-center">
                {deleteButton()}
            </td>
            <DeleteConfirmationModal
                show={showModal}
                title={'ยืนยันการลบข้อมูล'}
                description={'คุณแน่ใจหรือไม่ว่าต้องการลบข้อมูลนี้'}
                onHide={handleCloseModal}
                onConfirm={handleConfirmDelete}
            />
        </tr>
    );
};

export default PunishmentHistoryForm;